import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import { isDatasetActive } from '../../../utils/datasetReviewState';

/**
 * PRD-1359 — the dataset records behind `formData.reportDatasetIds`, for the
 * review-required banner on wizard step 4.
 *
 * Step 2 already holds a dataset list of its own (it needs one for the select),
 * so this exists for the later steps, which receive ids and nothing else.
 * Threading the records through formData instead would freeze the counts at the
 * moment step 2 was left — and step 4 is exactly where an author sits longest,
 * so that is the one place a stale count is most likely to be read.
 *
 * Polls on the same 5s cadence as the rest of the app, gated on isDatasetActive
 * so a wizard over settled datasets makes one request and then stops.
 *
 * @param {string[]} datasetIds ids from formData; order and type are not assumed.
 * @returns {object[]} the matching dataset records, empty until the first load.
 */
export function useReviewRequiredDatasets(datasetIds) {
  const [datasets, setDatasets] = useState([]);
  // Ids arrive as a fresh array each render, so depend on a stable key rather
  // than the array itself or the effect re-runs forever.
  const idKey = (datasetIds ?? []).map(String).join('|');

  const load = useCallback(async () => {
    try {
      const wanted = new Set(idKey ? idKey.split('|') : []);
      // Pages until every wanted id is accounted for, rather than reading page
      // one and hoping. `/datasets` has no id filter and sorts newest-first, so
      // a single page dropped older datasets silently: `wanted` matched
      // nothing, the notice got an empty array and returned null, and the
      // banner simply did not render — no error, no empty state, nothing in the
      // console. For a warning documented as "the only signal that an answer
      // was computed on partial data", failing silently is the worst shape.
      //
      // The early exit keeps the usual case at one request: an automation names
      // a handful of datasets, and they are almost always on page one.
      const matched = (items) => items.filter((d) => wanted.has(String(d.id)));
      const all = await apiService.getAllDatasets(
        {},
        (items) => matched(items).length >= wanted.size,
      );
      setDatasets(matched(all));
    } catch (err) {
      console.error('Failed to load datasets for review state:', err);
      // Leave the last good list in place: a transient failure must not make a
      // standing warning disappear.
    }
  }, [idKey]);

  useEffect(() => {
    if (!idKey) {
      setDatasets([]);
      return;
    }
    load();
  }, [idKey, load]);

  useEffect(() => {
    if (!datasets.some((d) => isDatasetActive(d))) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [datasets, load]);

  return datasets;
}

export default useReviewRequiredDatasets;
